import { ListItem, ListItemIcon, ListItemText } from '@material-ui/core';
import { Redeem } from '@material-ui/icons';
import { useMutation } from '@tanstack/react-query';
import moment from 'moment/min/moment-with-locales';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import strings from '../localization';
import AirdropService, { RateLimitError } from '../services/airdropService';
import { chromeLocalStorage } from '../utils/chrome-storage-polyfill';
import { useLocalStorage } from '../utils/hookHelpers';
import { useWallet } from '../utils/wallet';
import { TelegramPrefetchProps } from './BalancesList/TelegramSubscribeDialog';
import { CaptchaPrefetchProps } from './BalancesList/CaptchaDialog';

const DAY_IN_MILLS = 24 * 60 * 60 * 1000;

export interface WelcomeAirdropProps {
  onShowSubscription: () => void;
}

interface AirdropStorageProps {
  timestamp: number;
  count: number;
}

export function WelcomeAirdrop({ onShowSubscription }: WelcomeAirdropProps) {
  const wallet = useWallet();
  const { enqueueSnackbar } = useSnackbar();

  const [lastRequestTimestamp, setLastRequestTimestamp] = useState<number | undefined>();
  const [requestCount, setRequestCount] = useState(0);
  const [verified, setVerified] = useState(false);
  const [hcaptchaToken, setHcaptchaToken] = useState<string>('');
  const airdropService = new AirdropService();

  const canRequestAgain =
    requestCount < 5 &&
    (lastRequestTimestamp
      ? lastRequestTimestamp + DAY_IN_MILLS <= Date.now()
      : true);
  
  useLocalStorage('welcomeAirdropLastRequest', (value: AirdropStorageProps | undefined) => {
    setLastRequestTimestamp(value ? value.timestamp : undefined);
    setRequestCount(value ? value.count : 0);
  });

  useLocalStorage('tmpPrefetch', (props: CaptchaPrefetchProps | undefined) => {
    const created = props ? props.created > 0 : false;
    const hasToken = props ? !!props.hcaptchaToken : false;
    setVerified(created && hasToken);
    setHcaptchaToken(props?.hcaptchaToken || '');
  });

  const { mutate, isLoading } = useMutation({
    mutationFn: async () => {
      return await airdropService.register(wallet);
    },
    onSuccess: () => {
      enqueueSnackbar(strings.welcomeAirdropSnackbar.replace('%s', '1'), {
        variant: 'success',
        autoHideDuration: 5000,
      });
      
      // Clear the captcha token from storage after successful use
      chromeLocalStorage.remove(['tmpPrefetch']);
      setVerified(false);
      setHcaptchaToken('');
    },
    onError: (error: any) => {
      let errorMessage = error.message ?? strings.airdropIsAlreadyDoneToday;
      
      // Handle captcha-specific errors
      if (error.message?.includes('captcha')) {
        errorMessage = 'Captcha verification failed. Please complete the captcha again.';
        // Clear invalid captcha token
        chromeLocalStorage.remove(['tmpPrefetch']);
        setVerified(false);
        setHcaptchaToken('');
      }
      
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 5000,
      });
    },
    onSettled: (data, error) => {
      if (data || error instanceof RateLimitError) {
        const storageProps: AirdropStorageProps = {
          timestamp: Date.now(),
          count: requestCount + 1,
        };

        chromeLocalStorage.set({
          welcomeAirdropLastRequest: storageProps,
        });
      }
    },
  });

  const isDisabled = !canRequestAgain || isLoading;

  const onClick = () => {
    if (verified && hcaptchaToken) {
      mutate();
    } else {
      onShowSubscription();
    }
  };

  if (requestCount >= 5) {
    return null;
  }

  const getSecondaryText = () => {
    if (!canRequestAgain) {
      return strings.welcomeAirdropUnavailable.replace(
        '%s',
        moment().to(lastRequestTimestamp! + DAY_IN_MILLS),
      );
    }
    
    if (!verified) {
      return 'Complete captcha verification to claim airdrop';
    }
    
    return strings.welcomeAirdropAvailable;
  };

  return (
    <ListItem
      button
      onClick={() => onClick()}
      disabled={isDisabled}
    >
      <ListItemIcon>
        <Redeem />
      </ListItemIcon>

      <ListItemText
        primary={strings.welcomeAirdropTitle}
        primaryTypographyProps={{ style: { fontWeight: '600' } }}
        secondary={getSecondaryText()}
      />
    </ListItem>
  );
}